import { useEffect, useState, useCallback } from 'react'
import {
  Bell, RefreshCw, AlertTriangle, Send, Mail,
} from 'lucide-react'
import styles from './NotificationsPage.module.css'

const DR_API = import.meta.env.VITE_DR_API_BASE_URL ?? ''

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('dr_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ── Types ──────────────────────────────────────────────────────────────────────

type ChannelType = 'TEAMS' | 'SLACK' | 'PAGERDUTY' | 'EMAIL'

interface Channel {
  id: number
  name: string
  channel_type: ChannelType
  webhook_url_display: string
  api_key_display: string
  email_addresses: string
  is_active: boolean
  description: string
}

interface EventMapping {
  id: number
  event_type: string
  channel_name: string
  channel_type: ChannelType
  minimum_severity: string
  is_active: boolean
}

interface NotifLog {
  id: number
  event_type: string
  channel_name: string
  status: 'SENT' | 'FAILED' | 'PENDING'
  sent_at: string
  error_message: string | null
  retry_count: number
}

interface Toast {
  id: number
  message: string
  success: boolean
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function fmtEventType(raw: string): string {
  return raw
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

function ChannelIcon({ type }: { type: ChannelType }) {
  if (type === 'TEAMS') return <span className={`${styles.channelIcon} ${styles.channelIconTeams}`}>T</span>
  if (type === 'SLACK') return <span className={`${styles.channelIcon} ${styles.channelIconSlack}`}>#</span>
  if (type === 'PAGERDUTY') return <span className={`${styles.channelIcon} ${styles.channelIconPagerDuty}`}>P</span>
  return (
    <span className={`${styles.channelIcon} ${styles.channelIconEmail}`}>
      <Mail size={12} />
    </span>
  )
}

function ChannelTypeBadge({ type }: { type: ChannelType }) {
  const cls =
    type === 'TEAMS' ? styles.badgeBlue :
    type === 'SLACK' ? styles.badgePurple :
    type === 'PAGERDUTY' ? styles.badgeGreen :
    styles.badgeGray
  return <span className={cls}>{type}</span>
}

function StatusBadge({ status }: { status: NotifLog['status'] }) {
  if (status === 'SENT') return <span className={styles.badgeGreen}>Sent</span>
  if (status === 'FAILED') return <span className={styles.badgeRed}>Failed</span>
  return <span className={styles.badgeAmber}>Pending</span>
}

function SeverityBadge({ sev }: { sev: string }) {
  if (sev === 'CRITICAL') return <span className={styles.badgeRed}>{sev}</span>
  if (sev === 'HIGH') return <span className={styles.badgeAmber}>{sev}</span>
  return <span className={styles.badgeGray}>{sev}</span>
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function NotificationsPage() {
  const [channels, setChannels] = useState<Channel[]>([])
  const [mappings, setMappings] = useState<EventMapping[]>([])
  const [logs, setLogs] = useState<NotifLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toasts, setToasts] = useState<Toast[]>([])
  const [togglingId, setTogglingId] = useState<number | null>(null)
  const [testingId, setTestingId] = useState<number | null>(null)
  const [retryingId, setRetryingId] = useState<number | null>(null)

  const addToast = (message: string, success: boolean) => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, success }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000)
  }

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [cRes, mRes, lRes] = await Promise.all([
        fetch(`${DR_API}/api/v1/dr/notifications/channels/`, { headers: authHeaders() }),
        fetch(`${DR_API}/api/v1/dr/notifications/mappings/`, { headers: authHeaders() }),
        fetch(`${DR_API}/api/v1/dr/notifications/logs/`, { headers: authHeaders() }),
      ])
      if (!cRes.ok || !mRes.ok || !lRes.ok) throw new Error('API error')
      const [cData, mData, lData] = await Promise.all([cRes.json(), mRes.json(), lRes.json()])
      setChannels(cData.results ?? cData)
      setMappings(mData.results ?? mData)
      setLogs(lData.results ?? lData)
    } catch {
      setError('Cannot reach DR API. Make sure the dr-api service is running.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const handleToggle = async (ch: Channel) => {
    setTogglingId(ch.id)
    try {
      const res = await fetch(`${DR_API}/api/v1/dr/notifications/channels/${ch.id}/toggle/`, {
        method: 'POST',
        headers: authHeaders(),
      })
      if (!res.ok) throw new Error('Toggle failed')
      setChannels(prev => prev.map(c => c.id === ch.id ? { ...c, is_active: !c.is_active } : c))
    } catch {
      addToast('Failed to toggle channel', false)
    } finally {
      setTogglingId(null)
    }
  }

  const handleTest = async (ch: Channel) => {
    setTestingId(ch.id)
    try {
      const res = await fetch(`${DR_API}/api/v1/dr/notifications/channels/${ch.id}/test/`, {
        method: 'POST',
        headers: authHeaders(),
      })
      const data = await res.json().catch(() => ({ success: false, message: 'Unknown error' }))
      addToast(data.message ?? (data.success ? 'Test sent!' : 'Test failed'), !!data.success)
    } catch {
      addToast('Test notification failed', false)
    } finally {
      setTestingId(null)
    }
  }

  const handleRetry = async (log: NotifLog) => {
    setRetryingId(log.id)
    try {
      const res = await fetch(`${DR_API}/api/v1/dr/notifications/logs/${log.id}/retry/`, {
        method: 'POST',
        headers: authHeaders(),
      })
      if (!res.ok) throw new Error('Retry failed')
      addToast('Retry queued successfully', true)
      await fetchAll()
    } catch {
      addToast('Retry failed', false)
    } finally {
      setRetryingId(null)
    }
  }

  return (
    <div className={styles.page}>

      {/* ── Toasts ── */}
      <div className={styles.toastContainer}>
        {toasts.map(t => (
          <div key={t.id} className={`${styles.toast} ${t.success ? styles.toastSuccess : styles.toastError}`}>
            {t.success ? '✓' : '✕'} {t.message}
          </div>
        ))}
      </div>

      {/* ── Header ── */}
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroBreadcrumb}>
            <Bell size={12} />
            <span>Disaster Recovery</span>
            <span className={styles.breadSep}>/</span>
            <span>Notifications</span>
          </div>
          <h1 className={styles.heroTitle}>Notification Management</h1>
          <p className={styles.heroSub}>
            Configure notification channels, manage event-to-channel mappings, and monitor
            notification delivery logs.
          </p>
        </div>
        <button className={styles.btnSecondary} onClick={fetchAll} disabled={loading}>
          <RefreshCw size={13} className={loading ? styles.spin : ''} /> Refresh
        </button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className={styles.errorBanner}>
          <AlertTriangle size={15} />
          <span>{error}</span>
          <button className={styles.retryBtn} onClick={fetchAll}>Retry</button>
        </div>
      )}

      {/* ── Channels section ── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <Bell size={15} />
          <h2>Notification Channels</h2>
          <span className={styles.countBadge}>{channels.length} channels</span>
        </div>

        {loading && (
          <div className={styles.loadingRow}>
            <RefreshCw size={18} className={styles.spin} />
            <span>Loading channels…</span>
          </div>
        )}

        {!loading && channels.length === 0 && !error && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><Bell size={28} /></div>
            <p>No notification channels configured.</p>
          </div>
        )}

        {!loading && channels.length > 0 && (
          <div className={styles.channelsGrid}>
            {channels.map(ch => (
              <div key={ch.id} className={`${styles.channelCard} ${ch.is_active ? styles.channelCardActive : ''}`}>
                <div className={styles.channelCardTop}>
                  <ChannelIcon type={ch.channel_type} />
                  <div className={styles.channelCardInfo}>
                    <div className={styles.channelName}>{ch.name}</div>
                    <ChannelTypeBadge type={ch.channel_type} />
                  </div>
                  {/* Toggle switch */}
                  <button
                    className={`${styles.toggle} ${ch.is_active ? styles.toggleOn : styles.toggleOff}`}
                    onClick={() => handleToggle(ch)}
                    disabled={togglingId === ch.id}
                    title={ch.is_active ? 'Disable channel' : 'Enable channel'}
                  >
                    <span className={styles.toggleKnob} />
                  </button>
                </div>

                {ch.description && (
                  <div className={styles.channelDesc}>{ch.description}</div>
                )}

                {ch.webhook_url_display && (
                  <div className={styles.channelMeta}>
                    <span className={styles.channelMetaLabel}>Webhook:</span>
                    <span className={styles.channelMetaVal}>{ch.webhook_url_display}</span>
                  </div>
                )}
                {ch.email_addresses && (
                  <div className={styles.channelMeta}>
                    <span className={styles.channelMetaLabel}>Emails:</span>
                    <span className={styles.channelMetaVal}>{ch.email_addresses}</span>
                  </div>
                )}
                {ch.api_key_display && (
                  <div className={styles.channelMeta}>
                    <span className={styles.channelMetaLabel}>API Key:</span>
                    <span className={styles.channelMetaVal}>{ch.api_key_display}</span>
                  </div>
                )}

                <div className={styles.channelCardActions}>
                  <button
                    className={styles.testBtn}
                    onClick={() => handleTest(ch)}
                    disabled={testingId === ch.id || !ch.is_active}
                    title={!ch.is_active ? 'Enable channel first' : 'Send test notification'}
                  >
                    {testingId === ch.id
                      ? <RefreshCw size={12} className={styles.spin} />
                      : <Send size={12} />}
                    Send Test
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Event Mappings section ── */}
      <div className={styles.tableSection}>
        <div className={styles.sectionHeader}>
          <Bell size={15} />
          <h2>Event Mappings</h2>
          <span className={styles.countBadge}>{mappings.length} mappings</span>
        </div>

        {!loading && mappings.length === 0 && !error && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><Bell size={28} /></div>
            <p>No event mappings configured.</p>
          </div>
        )}

        {!loading && mappings.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Event Type</th>
                  <th>Channel</th>
                  <th>Channel Type</th>
                  <th>Min. Severity</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map(m => (
                  <tr key={m.id}>
                    <td><span className={styles.eventType}>{fmtEventType(m.event_type)}</span></td>
                    <td>{m.channel_name}</td>
                    <td><ChannelTypeBadge type={m.channel_type} /></td>
                    <td><SeverityBadge sev={m.minimum_severity} /></td>
                    <td>
                      {m.is_active
                        ? <span className={styles.badgeGreen}>Active</span>
                        : <span className={styles.badgeGray}>Inactive</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Notification Log section ── */}
      <div className={styles.tableSection}>
        <div className={styles.sectionHeader}>
          <Bell size={15} />
          <h2>Notification Log</h2>
          <span className={styles.countBadge}>{logs.length} entries</span>
        </div>

        {!loading && logs.length === 0 && !error && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><Bell size={28} /></div>
            <p>No notification log entries.</p>
          </div>
        )}

        {!loading && logs.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Event Type</th>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Sent At</th>
                  <th>Retries</th>
                  <th>Error</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id}>
                    <td><span className={styles.eventType}>{fmtEventType(l.event_type)}</span></td>
                    <td>{l.channel_name}</td>
                    <td><StatusBadge status={l.status} /></td>
                    <td className={styles.dateCell}>{fmtDate(l.sent_at)}</td>
                    <td className={styles.numCell}>{l.retry_count}</td>
                    <td className={styles.errorCell}>{l.error_message ?? '—'}</td>
                    <td>
                      {l.status === 'FAILED' && (
                        <button
                          className={styles.retryActionBtn}
                          onClick={() => handleRetry(l)}
                          disabled={retryingId === l.id}
                        >
                          {retryingId === l.id
                            ? <RefreshCw size={12} className={styles.spin} />
                            : <RefreshCw size={12} />}
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
